import { UserRoles } from '@troyai/auth/data-access';
import type { UserDetails } from '@troyai/auth/data-access';
import type { CareManagerUserProfileAPIResponse } from '@troyai/care-management/data-access';
import type { CarePlanTemplate } from '@troyai/case-notes/data-access';
import {
  DEMO_ACTORS,
  DemoAccount,
  DemoPerspective,
  SCRIPTED_ID_BASE,
} from '@troyai/portal/investor-demo/core';

import { seedCarePlanTemplates } from './fixtures/care-plan-templates.fixture';

/** What demo mode changes in the real `/me` and `/cm` answers. */

/** Flags the demoed screens check. */
export const DEMO_FLAGS_ON = [
  'care-console',
  'intervention-episodes',
  'case-notes',
  'hra-care-plans-ai-assistance',
];

/**
 * Off, so nothing gets in the way of the story: the tour and announcements. `demo-mode` (the PII
 * anonymizer for real screens) is not listed: the account keeps its own value. The scripted answers
 * never reach the anonymizer, but a real member's real requests (e.g. `GET members/<id>`) do, so
 * with the anonymizer on her name and date of birth are randomized like any real member's.
 */
export const DEMO_FLAGS_OFF = ['guided-tour', 'global-announcements'];

/** The cast's global ids, in the scripted range so they never meet a real user's. */
export const CAST_GLOBAL_IDS = {
  dana: SCRIPTED_ID_BASE + 990001,
  lisa: SCRIPTED_ID_BASE + 990002,
  agent: SCRIPTED_ID_BASE + 990003,
} as const;

/**
 * The signed-in user as the demo wants her: the real account, with the demo's flags and, in the
 * care perspective, the CM Internal role. A pharmacist keeps her own roles, so the screens that
 * check them hide what she can't use. Her email and name stay her own.
 */
export function patchMe(user: UserDetails): UserDetails {
  const featureFlags: Record<string, boolean> = { ...user.feature_flags };
  for (const flag of DEMO_FLAGS_ON) {
    featureFlags[flag] = true;
  }
  for (const flag of DEMO_FLAGS_OFF) {
    featureFlags[flag] = false;
  }
  const roles = user.roles ?? [];
  const asCareManager = perspectiveFor(user) === 'care' && !roles.includes(UserRoles.CMInternal);
  return {
    ...user,
    roles: asCareManager ? [...roles, UserRoles.CMInternal] : roles,
    feature_flags: featureFlags,
  };
}

/**
 * Whose story the signed-in account sees, read from the account as the backend sent it (before
 * `patchMe`): the pharmacy's for a Pharmacy User who is not also a CM Internal, else the care
 * manager's.
 */
export function perspectiveFor(user: UserDetails): DemoPerspective {
  const roles = user.roles ?? [];
  return roles.includes(UserRoles.PharmacyUser) && !roles.includes(UserRoles.CMInternal)
    ? 'pharmacy'
    : 'care';
}

/** `{ data: UserDetails }` as the API sends it; anything else is returned untouched. */
export function patchMeResponse(body: unknown): unknown {
  if (typeof body !== 'object' || body === null || !('data' in body)) {
    return body;
  }
  const data = (body as { data: unknown }).data;
  return typeof data === 'object' && data !== null
    ? { ...body, data: patchMe(data as UserDetails) }
    : body;
}

/** A trimmed string field of an object, or '' when it is not one. */
function textOf(value: unknown, key: string): string {
  const field =
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : null;
  return typeof field === 'string' ? field.trim() : '';
}

/**
 * The account the pharmacy demo runs as, from the real `me` and `profile` answers (each the
 * answer's `data`): the email of `me` and the full name of `profile`, or the email when the
 * profile gives no name or failed (null); and the name of the pharmacy the sidebar shows, the
 * selected one of `me`'s pharmacies, else its first. Null when `me` holds no email.
 */
export function accountFrom(
  me: unknown,
  profile: unknown,
  selectedPharmacyId: number | null = null
): DemoAccount | null {
  const email = textOf(me, 'email');
  if (!email) {
    return null;
  }
  const name = `${textOf(profile, 'first_name')} ${textOf(profile, 'last_name')}`.trim();
  const listed = typeof me === 'object' && me !== null ? (me as UserDetails).pharmacies : null;
  const pharmacies = Array.isArray(listed) ? listed : [];
  const pharmacy = textOf(
    pharmacies.find((candidate) => candidate?.global_id === selectedPharmacyId) ?? pharmacies[0],
    'name'
  );
  return { email, name: name || email, ...(pharmacy ? { pharmacy } : {}) };
}

/** The three demo actors as care managers, under their CAST_GLOBAL_IDS. */
const DEMO_CARE_MANAGERS: CareManagerUserProfileAPIResponse[] = (
  ['dana', 'lisa', 'agent'] as const
).map((key) => ({
  global_id: CAST_GLOBAL_IDS[key],
  name: DEMO_ACTORS[key].name,
  email: DEMO_ACTORS[key].email,
}));

/** The real care-manager list plus the demo actors, so owners and actors get names. */
export function patchCareManagersResponse(body: unknown): unknown {
  if (typeof body !== 'object' || body === null || !('data' in body)) {
    return body;
  }
  const data = (body as { data: unknown }).data;
  const real = Array.isArray(data) ? (data as CareManagerUserProfileAPIResponse[]) : [];
  const known = new Set(real.map((manager) => (manager.email ?? '').toLowerCase()));
  return {
    ...body,
    data: [...real, ...DEMO_CARE_MANAGERS.filter((manager) => !known.has(manager.email))],
  };
}

/**
 * The real care plan templates plus the demo's own. The HRA drawer opens a recommendation for
 * review only when its template is in this list, and the demo's recommendations point at the demo
 * templates.
 */
export function patchCarePlanTemplatesResponse(body: unknown): unknown {
  if (typeof body !== 'object' || body === null || !('data' in body)) {
    return body;
  }
  const data = (body as { data: unknown }).data;
  const real = Array.isArray(data) ? (data as CarePlanTemplate[]) : [];
  const known = new Set(real.map((template) => template.id));
  return {
    ...body,
    data: [...real, ...seedCarePlanTemplates().filter((template) => !known.has(template.id))],
  };
}
